import { useQuery } from '@tanstack/react-query';
import { groupKeys, type MembershipRequestView } from '../../../lib/queries/groups';
import { useMember } from '../../../lib/use-session';

// Single source of truth for the join requests a leader may actually act on.
//
// A leader reviews OTHERS' requests, never their own: the server rejects
// deciding your own request (#46), so the reviewable list hides the viewer's
// own pending request. The sidebar badge count MUST exclude it too — otherwise
// the badge reads one higher than the visible list (a network leader who
// requested to join a child circle sees badge = N+1, list = N). Deriving both
// the count and the list from this one hook makes that drift impossible.
//
// Both circle and network queries are declared (rules of hooks) but only the
// active `kind` fetches; the inactive one is a disabled no-op.
export function useReviewableJoinRequests(
  kind: 'circle' | 'network',
  groupId: string,
): { requests: MembershipRequestView[]; isLoading: boolean } {
  const viewerId = useMember()?.id;
  const circleQuery = useQuery({
    ...groupKeys.circle.joinRequests(groupId),
    enabled: kind === 'circle',
  });
  const networkQuery = useQuery({
    ...groupKeys.network.joinRequests(groupId),
    enabled: kind === 'network',
  });
  const query = kind === 'circle' ? circleQuery : networkQuery;
  const requests = (query.data?.items ?? []).filter(
    (r) => r.subject.userId !== viewerId,
  );
  return { requests, isLoading: query.data === undefined };
}

// The accepted/declined history slice for a given status, self-filtered to
// exclude the viewer's own request (a leader can't decide their own — #46 —
// so their own row would offer a reverse-approve that 409s). Both kind variants
// are declared (rules of hooks); only the active `kind` fetches. Fetched
// unconditionally so the review filter's per-status counts stay live.
export function useLeaderRequestsByStatus(
  kind: 'circle' | 'network',
  groupId: string,
  status: 'accepted' | 'declined',
): { requests: MembershipRequestView[]; isLoading: boolean } {
  const viewerId = useMember()?.id;
  const circleQuery = useQuery({
    ...groupKeys.circle.joinRequestsByStatus(groupId, status),
    enabled: kind === 'circle',
  });
  const networkQuery = useQuery({
    ...groupKeys.network.joinRequestsByStatus(groupId, status),
    enabled: kind === 'network',
  });
  const query = kind === 'circle' ? circleQuery : networkQuery;
  const requests = (query.data?.items ?? []).filter(
    (r) => r.subject.userId !== viewerId,
  );
  return { requests, isLoading: query.data === undefined };
}
